/**
 * Avatar - Main avatar component with integrated speech and TTS
 *
 * ARCHITECTURE:
 * This component serves as the bridge between:
 * - React UI (chat interface, speaking indicator)
 * - Speech Queue Store (manages what to say and when)
 * - AvatarCanvas (Three.js 3D rendering and lip sync)
 * - TTS Service (generates audio from text)
 *
 * SPEECH FLOW:
 * 1. Component mounts → registers processSpeechItem with speech queue
 * 2. Other code calls avatarSpeak("text") → enqueues speech item
 * 3. Queue calls processSpeechItem → generates TTS audio
 * 4. AvatarCanvas plays audio and animates mouth
 * 5. On audio end → queue marks complete, processes next item
 */

import { useEffect, useRef, useCallback, useState, FormEvent } from 'react'
import { useAssessmentStore } from '../store/assessmentStore'
import { handleGeneralConversation } from '../services/conversation'
import { generateSpeech } from '../services/tts'
import { useSpeechQueueStore, avatarSpeak, avatarClearQueue, avatarSpeakAndWait } from '../store/speechQueueStore'
import { useSpeechRecognition } from '../hooks/useSpeechRecognition'
import AvatarCanvas from './AvatarCanvas'
import type { SpeechItem } from '@really-assess/shared/types'
import type { LipSyncPoint } from '../services/tts'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface AvatarProps {
  onReady?: () => void
  onSpeakComplete?: () => void
  onUserMessage?: (message: string) => void
  className?: string
  /** When false, hides the chatbox — avatar speaks instructions only */
  showChat?: boolean
}

/** Audio data passed to AvatarCanvas for playback */
interface AudioPlaybackData {
  url: string
  lipSync: LipSyncPoint[]
  messageId: string
}

// Extend window for global avatar methods (backwards compatibility)
declare global {
  interface Window {
    avatarSpeak?: (text: string, priority?: 'normal' | 'high') => string
    avatarSpeakDirect?: (text: string) => void
    avatarUnlock?: () => void
    avatarOnUserMessage?: (callback: (message: string) => void) => () => void
    avatarClearQueue?: () => void
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// User Message Handler Registry (for external code to listen for chat input)
// ─────────────────────────────────────────────────────────────────────────────

let userMessageHandlers: ((message: string) => void)[] = []

function notifyUserMessageHandlers(message: string) {
  userMessageHandlers.forEach((handler) => handler(message))
}

function registerUserMessageHandler(handler: (message: string) => void): () => void {
  userMessageHandlers.push(handler)
  return () => {
    userMessageHandlers = userMessageHandlers.filter((h) => h !== handler)
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function Avatar({
  onReady,
  onSpeakComplete,
  onUserMessage,
  className = '',
  showChat = true,
}: AvatarProps) {
  const { avatarState, setAvatarState, learner } = useAssessmentStore()

  // Speech queue integration
  const {
    isSpeaking,
    setSpeakFunction,
    markComplete,
    markFailed,
    _cleanup: cleanupSpeechQueue,
  } = useSpeechQueueStore()

  // Audio data for AvatarCanvas to play
  const [audioData, setAudioData] = useState<AudioPlaybackData | null>(null)

  // Track when 3D canvas is ready
  const [isCanvasReady, setIsCanvasReady] = useState(false)

  // Chat interface state
  const [chatInput, setChatInput] = useState('')
  const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'assistant'; text: string }[]>([])

  // Stable refs for callbacks (prevent stale closures in effects)
  const callbacksRef = useRef({ onReady, onSpeakComplete, onUserMessage })
  callbacksRef.current = { onReady, onSpeakComplete, onUserMessage }

  // ─────────────────────────────────────────────────────────────────
  // Canvas Ready Handler
  // ─────────────────────────────────────────────────────────────────

  const handleCanvasReady = useCallback(() => {
    setIsCanvasReady(true)
    setAvatarState({ isReady: true, audioUnlocked: true }) // Same-origin = no unlock needed
    callbacksRef.current.onReady?.()
  }, [setAvatarState])

  // ─────────────────────────────────────────────────────────────────
  // Speech Processing (called by speech queue for each item)
  // ─────────────────────────────────────────────────────────────────

  const processSpeechItem = useCallback(
    async (item: SpeechItem) => {
      try {
        setAvatarState({ isSpeaking: true })

        // Generate TTS audio + lip sync data
        const response = await generateSpeech(item.text)

        if (!response.audioUrl) {
          console.warn('[Avatar] TTS returned no audio — skipping playback')
          setAvatarState({ isSpeaking: false })
          markComplete(item.messageId)
          return
        }

        // Pass to canvas for playback
        setAudioData({
          url: response.audioUrl,
          lipSync: response.lipSync,
          messageId: item.messageId,
        })
      } catch (error) {
        console.error('[Avatar] TTS generation failed:', error)
        setAvatarState({ isSpeaking: false })
        markFailed(item.messageId, error instanceof Error ? error.message : 'TTS failed')
      }
    },
    [setAvatarState, markFailed, markComplete]
  )

  // Register speech processor with queue on mount
  useEffect(() => {
    setSpeakFunction(processSpeechItem)
    return () => setSpeakFunction(null)
  }, [setSpeakFunction, processSpeechItem])

  // ─────────────────────────────────────────────────────────────────
  // Audio Playback Handlers
  // ─────────────────────────────────────────────────────────────────

  const handleAudioStart = useCallback(() => {
    setAvatarState({ isSpeaking: true })
  }, [setAvatarState])

  const handleAudioEnd = useCallback(() => {
    setAvatarState({ isSpeaking: false })

    if (audioData) {
      markComplete(audioData.messageId)
      callbacksRef.current.onSpeakComplete?.()
    }

    setAudioData(null)
  }, [setAvatarState, markComplete, audioData])

  // Sync queue's isSpeaking state to assessment store
  useEffect(() => {
    setAvatarState({ isSpeaking })
  }, [isSpeaking, setAvatarState])

  // ─────────────────────────────────────────────────────────────────
  // Legacy Global Methods (backwards compatibility)
  // ─────────────────────────────────────────────────────────────────

  const speak = useCallback((text: string, priority: 'normal' | 'high' = 'normal') => {
    return avatarSpeak(text, priority)
  }, [])

  const speakDirect = useCallback((text: string) => {
    avatarSpeak(text, 'normal')
  }, [])

  const unlockAudio = useCallback(() => {
    setAvatarState({ audioUnlocked: true })
  }, [setAvatarState])

  // Expose methods on window for non-React code
  useEffect(() => {
    window.avatarSpeak = speak
    window.avatarSpeakDirect = speakDirect
    window.avatarUnlock = unlockAudio
    window.avatarOnUserMessage = registerUserMessageHandler
    window.avatarClearQueue = avatarClearQueue

    return () => {
      delete window.avatarSpeak
      delete window.avatarSpeakDirect
      delete window.avatarUnlock
      delete window.avatarOnUserMessage
      delete window.avatarClearQueue
    }
  }, [speak, speakDirect, unlockAudio])

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanupSpeechQueue()
  }, [cleanupSpeechQueue])

  // ─────────────────────────────────────────────────────────────────
  // Chat/Conversation Handling
  // ─────────────────────────────────────────────────────────────────

  /** Process a user message - either route to handler or respond via TTS */
  const handleConversation = useCallback(
    async (message: string) => {
      callbacksRef.current.onUserMessage?.(message)

      // If external handlers registered, let them handle it
      if (userMessageHandlers.length > 0) {
        notifyUserMessageHandlers(message)
      } else {
        // Otherwise, generate a conversational response
        const response = await handleGeneralConversation(message, learner?.name)
        setChatMessages((prev) => [...prev, { role: 'assistant', text: response }])
        avatarSpeak(response, 'normal')
      }

      // Increment conversation count and auto-minimize after 2 conversations
      const newCount = avatarState.conversationCount + 1
      setAvatarState({ conversationCount: newCount })
      if (newCount >= 2 && !avatarState.isMinimized) {
        // Auto-minimize after a short delay to let the speech complete
        setTimeout(() => {
          setAvatarState({ isMinimized: true })
        }, 2000)
      }
    },
    [learner, avatarState.conversationCount, avatarState.isMinimized, setAvatarState]
  )

  /** Handle chat form submission */
  const handleChatSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault()
      const message = chatInput.trim()
      if (!message) return

      setChatInput('')
      setChatMessages((prev) => [...prev, { role: 'user', text: message }])
      await handleConversation(message)
    },
    [chatInput, handleConversation]
  )

  // ─────────────────────────────────────────────────────────────────
  // Voice Input (Speech Recognition)
  // ─────────────────────────────────────────────────────────────────

  const handleSpeechEnd = useCallback(
    (transcript: string) => {
      if (transcript.trim()) {
        setChatInput('')
        setChatMessages((prev) => [...prev, { role: 'user', text: transcript }])
        handleConversation(transcript)
      }
    },
    [handleConversation]
  )

  const {
    isListening,
    transcript: speechTranscript,
    startListening,
    stopListening,
    isSupported: isSpeechSupported,
  } = useSpeechRecognition({
    onSpeechEnd: handleSpeechEnd,
    isAvatarSpeaking: () => avatarState.isSpeaking,
  })

  // Show interim transcript in input field
  useEffect(() => {
    if (isListening && speechTranscript) {
      setChatInput(speechTranscript)
    }
  }, [isListening, speechTranscript])

  const toggleVoiceInput = useCallback(() => {
    if (!isSpeechSupported) {
      alert('Speech recognition is not supported in this browser.')
      return
    }

    if (isListening) {
      stopListening()
    } else {
      setChatInput('')
      startListening()
    }
  }, [isListening, isSpeechSupported, startListening, stopListening])

  // Listen for custom events (from external code)
  useEffect(() => {
    const handleUserMessageEvent = (event: CustomEvent<{ message: string }>) => {
      handleConversation(event.detail.message)
    }

    window.addEventListener('avatar-user-message', handleUserMessageEvent as EventListener)
    return () => {
      window.removeEventListener('avatar-user-message', handleUserMessageEvent as EventListener)
    }
  }, [handleConversation])

  // ─────────────────────────────────────────────────────────────────
  // Toggle Minimize
  // ─────────────────────────────────────────────────────────────────

  const toggleMinimize = useCallback(() => {
    setAvatarState({ isMinimized: !avatarState.isMinimized })
  }, [avatarState.isMinimized, setAvatarState])

  // ─────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────

  // Minimized view - compact avatar bar with full chat interface
  if (avatarState.isMinimized) {
    return (
      <div className={`relative flex flex-col ${className}`}>
        {/* Minimized Avatar Bar */}
        <button
          onClick={toggleMinimize}
          className="w-full bg-assess-card border border-assess-border rounded-t-2xl p-3 flex items-center gap-3 hover:bg-assess-card/80 transition-colors"
        >
          {/* Mini avatar circle */}
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shrink-0 overflow-hidden">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
          </div>

          {/* Status text */}
          <div className="flex-1 text-left">
            <p className="text-white font-medium text-sm">Stella</p>
            <p className="text-gray-400 text-xs">
              {avatarState.isSpeaking ? 'Speaking...' : 'Click to expand'}
            </p>
          </div>

          {/* Speaking indicator */}
          {avatarState.isSpeaking && (
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse" />
              <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
              <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
            </div>
          )}

          {/* Expand icon */}
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
        </button>

        {/* Hidden canvas to keep TTS/audio working */}
        <div className="sr-only">
          <AvatarCanvas
            onReady={handleCanvasReady}
            audioUrl={audioData?.url}
            lipSync={audioData?.lipSync}
            onAudioStart={handleAudioStart}
            onAudioEnd={handleAudioEnd}
            className="w-1 h-1"
          />
        </div>

        {showChat && (
        <div className="bg-white/95 backdrop-blur-sm p-4 rounded-b-2xl shadow-lg">
          {/* Message History */}
          {chatMessages.length > 0 && (
            <div className="max-h-32 overflow-y-auto mb-3 space-y-2">
              {chatMessages.map((msg, i) => (
                <div
                  key={i}
                  className={`p-2 rounded-lg text-sm ${
                    msg.role === 'user'
                      ? 'bg-primary-500 text-white ml-8 text-right'
                      : 'bg-gray-100 text-gray-800 mr-8'
                  }`}
                >
                  {msg.text}
                </div>
              ))}
            </div>
          )}

          {/* Input Form */}
          <form onSubmit={handleChatSubmit} className="flex gap-2">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder={isListening ? 'Listening...' : 'Type a message...'}
              className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-800"
            />
            {/* Voice Input Button */}
            <button
              type="button"
              onClick={toggleVoiceInput}
              disabled={avatarState.isSpeaking}
              className={`px-4 py-2 rounded-lg transition-colors ${
                isListening
                  ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              title={isListening ? 'Stop listening' : 'Start voice input'}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" x2="12" y1="19" y2="22" />
              </svg>
            </button>
            {/* Send Button */}
            <button
              type="submit"
              disabled={!chatInput.trim() || avatarState.isSpeaking}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Send
            </button>
          </form>
        </div>
        )}
      </div>
    )
  }

  return (
    <div className={`relative flex flex-col ${className}`}>
      {/* 3D Avatar Area */}
      <div className="relative flex-1 min-h-0">
        {/* Loading Overlay */}
        {!isCanvasReady && (
          <div className="absolute inset-0 bg-assess-card rounded-t-2xl flex items-center justify-center z-10">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Loading Stella...</p>
            </div>
          </div>
        )}

        {/* Speaking Indicator */}
        {avatarState.isSpeaking && (
          <div className="absolute top-4 left-4 bg-primary-500/80 px-3 py-1 rounded-full text-sm flex items-center gap-2 z-20">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            Speaking...
          </div>
        )}

        {showChat && (
        <button
          onClick={toggleMinimize}
          className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-2 rounded-lg z-20 transition-colors"
          title="Minimize Stella"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>
        )}

        {/* Three.js Avatar Canvas */}
        <AvatarCanvas
          onReady={handleCanvasReady}
          audioUrl={audioData?.url}
          lipSync={audioData?.lipSync}
          onAudioStart={handleAudioStart}
          onAudioEnd={handleAudioEnd}
          className="w-full h-full rounded-t-2xl"
        />
      </div>

      {showChat && (
      <div className="bg-white/95 backdrop-blur-sm p-4 rounded-b-2xl shadow-lg">
        {/* Message History */}
        {chatMessages.length > 0 && (
          <div className="max-h-32 overflow-y-auto mb-3 space-y-2">
            {chatMessages.map((msg, i) => (
              <div
                key={i}
                className={`p-2 rounded-lg text-sm ${
                  msg.role === 'user'
                    ? 'bg-primary-500 text-white ml-8 text-right'
                    : 'bg-gray-100 text-gray-800 mr-8'
                }`}
              >
                {msg.text}
              </div>
            ))}
          </div>
        )}

        {/* Input Form */}
        <form onSubmit={handleChatSubmit} className="flex gap-2">
          <input
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            placeholder={isListening ? 'Listening...' : 'Type a message...'}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-800"
          />
          {/* Voice Input Button */}
          <button
            type="button"
            onClick={toggleVoiceInput}
            disabled={avatarState.isSpeaking}
            className={`px-4 py-2 rounded-lg transition-colors ${
              isListening
                ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            title={isListening ? 'Stop listening' : 'Start voice input'}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" x2="12" y1="19" y2="22" />
            </svg>
          </button>
          {/* Send Button */}
          <button
            type="submit"
            disabled={!chatInput.trim() || avatarState.isSpeaking}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Send
          </button>
        </form>
      </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook for Other Components
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to control the avatar from other components.
 * Provides speak, speakAndWait, speakWarning, and queue management functions.
 */
export function useAvatar() {
  const speak = useCallback((text: string, priority: 'normal' | 'high' = 'normal') => {
    return avatarSpeak(text, priority)
  }, [])

  /**
   * Speak and wait for completion before continuing.
   * Use this when you need to wait for speech to finish before taking an action.
   *
   * @example
   * await speakAndWait("Welcome!")
   * setStep('next') // Only runs after speech completes
   */
  const speakAndWait = useCallback((text: string, priority: 'normal' | 'high' = 'normal') => {
    return avatarSpeakAndWait(text, priority)
  }, [])

  const speakWarning = useCallback((text: string) => {
    return avatarSpeak(text, 'high')
  }, [])

  const unlockAudio = useCallback(() => {
    window.avatarUnlock?.()
  }, [])

  const onUserMessage = useCallback((callback: (message: string) => void) => {
    return window.avatarOnUserMessage?.(callback) ?? (() => {})
  }, [])

  const clearQueue = useCallback(() => {
    avatarClearQueue()
  }, [])

  return { speak, speakAndWait, speakWarning, unlockAudio, onUserMessage, clearQueue }
}
